import { useContext, useEffect, useState, FC, useRef } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import clsx from 'clsx'

import Auth from 'store/auth'
import WebinarSocketContext from 'context/webinarSocketContext'
import ChatInput from './chat-input'
import useNMO from 'store/analytics-nmo/useNMO'

import { cutHashFromURL, getDigitFromString } from 'utils/getURLData'
import { popupName } from 'constants/auth/auth-data'
import { ABType, STATIC_URL, StatusVocabulary } from 'constants/data'
import { buttonIdWebinarEnded } from 'constants/clickableElementsId'
import { PLACEFLAG_CHAT } from 'constants/analytic-data'
import { GapMessage } from '../../player/data'
import { webinarsForStudents } from 'components/students/students-data'
import { IChatMessage } from 'constants/types/webinar.types'

import styles from './chat-bottom.module.scss'
import Button from 'components/parts/button/button'
import ButtonThemes from 'components/parts/button/themes'
import Image from 'next/image'
import PopupWrapperNew from 'containers/popup-wrapper-new/popup-wrapper-new'
import { ScreenSize } from 'constants/media-constanst'
import usePCWindow from 'hooks/usePCWindow'

interface IChatBottom {
  msgReplyTo: IChatMessage | null
  handleCancelReply: () => void
}

const ChatBottom: FC<IChatBottom> = ({ msgReplyTo, handleCancelReply }: IChatBottom) => {
  const { webinarStatus, handleWebSocketSend, isBlocked } = useContext(WebinarSocketContext)
  const { isAuthorized, isFilledUser, isSponsor } = Auth.useAuth()
  const router = useRouter()
  const materialId = Number(getDigitFromString(router.query.id as string))
  const isChatForStudent = webinarsForStudents.includes(materialId)
  const isMobile = usePCWindow(ScreenSize.mobile)

  const { setSource } = useNMO()
  const [gapText, setGapText] = useState('')

  const handleAnalyticSource = () => setSource(PLACEFLAG_CHAT)

  const path = cutHashFromURL(router.asPath)
  const isHourBefore = webinarStatus === StatusVocabulary.HOUR_BEFORE
  const isEnded = webinarStatus === StatusVocabulary.PAST

  const [isMessageBlockOpen, setIsMessageBlockOpen] = useState(false)
  const closeHandler = () => {
    handleCancelReply()
    setIsMessageBlockOpen(false)
  }
  const openHandler = () => {
    setIsMessageBlockOpen(true)
  }

  // Рефы для отслеживания свайпа
  const popupRef = useRef<HTMLDivElement>(null)
  const touchStartY = useRef<number | null>(null)
  const touchStartX = useRef<number | null>(null)
  const touchMoveY = useRef<number | null>(null)
  const touchMoveX = useRef<number | null>(null)

  const handleTouchStart = (e: TouchEvent) => {
    if (popupRef.current && popupRef.current.contains(e.target as Node)) {
      touchStartY.current = e.touches[0].clientY
      touchStartX.current = e.touches[0].clientX
    }
  }

  const handleTouchMove = (e: TouchEvent) => {
    if (popupRef.current && popupRef.current.contains(e.target as Node)) {
      touchMoveY.current = e.touches[0].clientY
      touchMoveX.current = e.touches[0].clientX
    }
  }

  const handleTouchEnd = (e: TouchEvent) => {
    if (popupRef.current && popupRef.current.contains(e.target as Node)) {
      if (touchMoveY.current && touchStartY.current && touchMoveX.current && touchStartX.current) {
        const deltaY = touchMoveY.current - touchStartY.current
        const deltaX = Math.abs(touchMoveX.current - touchStartX.current)

        if (deltaY > 30 && deltaX < 50) {
          closeHandler()
        }
      }
    }

    // Сбрасываем значения
    touchStartY.current = null
    touchStartX.current = null
    touchMoveY.current = null
    touchMoveX.current = null
  }

  useEffect(() => {
    let message = ''
    if (!isAuthorized) message = GapMessage.NOT_AUTHORIZED
    else if (isBlocked) message = GapMessage.BLOCKED
    else if (!isFilledUser && !isSponsor && !isChatForStudent) message = GapMessage.NOT_FILLED
    else if (isHourBefore) message = GapMessage.HOUR_BEFORE
    else if (isEnded) message = GapMessage.ENDED

    setGapText(message)
  }, [isAuthorized, isFilledUser, isSponsor, isBlocked, isHourBefore, isEnded, isChatForStudent])

  // Добавляем слушатели только когда попап открыт
  useEffect(() => {
    if (isMessageBlockOpen || msgReplyTo) {
      document.addEventListener('touchstart', handleTouchStart, { passive: true })
      document.addEventListener('touchmove', handleTouchMove, { passive: true })
      document.addEventListener('touchend', handleTouchEnd, { passive: true })

      return () => {
        document.removeEventListener('touchstart', handleTouchStart)
        document.removeEventListener('touchmove', handleTouchMove)
        document.removeEventListener('touchend', handleTouchEnd)
      }
    }
  }, [isMessageBlockOpen, msgReplyTo])

  return (
    <>
      {!gapText ? (
        <>
          <div className={styles.chatWrapperDesk}>
            <ChatInput
              handleWebSocketSend={handleWebSocketSend}
              msgReplyTo={msgReplyTo}
              handleCancelReply={handleCancelReply}
              handleCancelInput={closeHandler}
            />
          </div>
          <div className={styles.chatWrapperMob}>
            <div className={styles.btnWrapper} onClick={openHandler}>
              <Button theme={ButtonThemes.SECOND}>
                <div className={styles.btnInner}>
                  <div>Написать в чат</div>
                  <Image
                    src={`${STATIC_URL}/comment/icon_comment_grey.svg`}
                    width="16"
                    height="16"
                    alt="Иконка чат"
                    className={styles.btnIcon}
                  ></Image>
                </div>
              </Button>
            </div>
            {isMobile && (isMessageBlockOpen || msgReplyTo) && (
              <PopupWrapperNew
                popupBlockMod={clsx(styles.popupBlockMob, {
                  [styles.popupBlockMobOpen]: isMessageBlockOpen,
                })}
                closePopup={closeHandler}
                isPopupScrollPosition={false}
                isBottom={true}
                btnCloseMod={styles.closeLine}
                popupRef={popupRef}
              >
                <ChatInput
                  handleWebSocketSend={handleWebSocketSend}
                  msgReplyTo={msgReplyTo}
                  handleCancelReply={handleCancelReply}
                  handleCancelInput={closeHandler}
                />
              </PopupWrapperNew>
            )}
          </div>
        </>
      ) : (
        <div
          className={clsx(styles.chatInputBlock, styles.chatInputUnauth, {
            [styles.isNotFilled]: !isAuthorized || (isAuthorized && !isFilledUser),
          })}
          id={isEnded ? buttonIdWebinarEnded : ''}
        >
          {gapText}
          {isAuthorized && !isFilledUser && (
            <Link
              href={`${path}#${popupName.anketaWebinar}`}
              className={styles.link}
              onClick={handleAnalyticSource}
            >
              Заполнить
            </Link>
          )}
        </div>
      )}
    </>
  )
}

export default ChatBottom
